import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { splitExpiry } from 'features/payment/utils';
import { GraphQLError } from 'graphql';

import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { IEncryptionResult } from 'components/payments/integrations/orbital/components/encryption/types';
import { useEnableCheckoutV2Experiment } from 'experiments/checkout-v2/use-enable-checkout-v2-experiment';
import {
  IAddAccountInput,
  IAddOrbitalAccountInput,
  IMergePrepaidInput,
  IMergePrepaidPayload,
  IsoCountryCode,
  PaymentProcessor,
  UserAccountsDocument,
  useDeletePaymentMethodMutation,
  useMergePrepaidMutation,
  useUserAccountsQuery,
} from 'generated/graphql-gateway';
import { usePrepaidsReload } from 'hooks/prepaid';
import { getNonce } from 'remote/api/first-data';
import { HttpErrorCodes } from 'remote/constants';
import {
  CustomEventNames,
  EventTypes,
  logEvent,
  logRBIEvent,
  updateAmplitudeUserAttributes,
} from 'state/amplitude';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useCanUseGooglePay } from 'state/google-pay/hooks/use-can-use-google-pay';
import { useGooglePay } from 'state/google-pay/hooks/use-google-pay';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { storePrepaidCard } from 'utils/encryption';
import { getConfigValue } from 'utils/environment';
import { toError } from 'utils/errors';
import { sanitizeAlphanumeric, sanitizeNumber } from 'utils/form';
import { ISOs } from 'utils/form/constants';
import logger from 'utils/logger';

import { CASH_ACCOUNT_IDENTIFIER } from '../constants';
import {
  IAddPaymentMethodOptions,
  IPaymentMethod,
  IPaymentPayload,
  IPaymentState,
  IReloadPrepaidCard,
} from '../types';

import { getPaymentMethodsState } from './getPaymentMethodsState';
import {
  filterExpiredCreditCards,
  transformPaymentDetailsToPaymentMethod,
} from './getPaymentMethodsState/utils';
import { IInitPaymentMethods, IPayment } from './types';

const cashAccount: IPaymentMethod = {
  cash: true,
  fdAccountId: CASH_ACCOUNT_IDENTIFIER,
  accountIdentifier: CASH_ACCOUNT_IDENTIFIER,
  orbitalIdentifier: null,
  credit: null,
  prepaid: null,
  paypal: null,
  venmo: null,
  cashapp: null,
};

/** @deprecated do not use this hook directly it is a singleton. use usePaymentContext/PaymentProvider instead */
export const _usePayment = ({
  getEncryptionDetailsMutation,
  openErrorDialog,
  user,
  isReAuthenticating,
  updateUserInfo,
  addCreditAccountMutation,
  paymentDetails,
  isQuickPayMethodSelected,
}: IPayment) => {
  // There is a conflict where the checkout v2 always uses payment redesign flow regardless of the payment redesign experiment status.
  // It means, if the payment redesign experiment is disabled but the checkout v2 experiment is enabled, then the payment redesign flow will be used internally and we want this behavior.
  // WARNING: If we finish the payment redesign experiment first, then we also may want to keep this validation using only the checkout v2 experiment value here to avoid issues.
  const isCheckoutV2Enabled = useEnableCheckoutV2Experiment();

  const { feCountryCode } = useLocale();
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [hasGetPaymentMethodsError, setHasGetPaymentMethodsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { applePayCardDetails, canUseApplePay } = useApplePay({});
  const { canUseGooglePay } = useCanUseGooglePay();
  const { googlePayCardDetails } = useGooglePay();
  const enableCashPayment = useFlag(LaunchDarklyFlag.ENABLE_CASH_PAYMENT);
  const enableCashApp = useFlag(LaunchDarklyFlag.ENABLE_CASH_APP_PAYMENTS);
  const enablePayPal = useFlag(LaunchDarklyFlag.ENABLE_PAYPAL_PAYMENTS);
  const enableVenmo = useFlag(LaunchDarklyFlag.ENABLE_VENMO_PAYMENTS);
  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);
  const [fireDeleteAccountMutation] = useDeletePaymentMethodMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
  });
  const [fireMergePrepaidMutation] = useMergePrepaidMutation({
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
  });

  const fdAccessTokenFirstRequest = useRef<string | null>(null);

  const { braintreeAuthorizationKey, baseUrl: fdUrl } = getConfigValue({
    key: 'firstData',
    defaultValue: {},
  });

  const {
    data: userAccountsData,
    loading: accountsLoading,
    refetch: refetchPaymentMethods,
  } = useUserAccountsQuery({
    variables: { feCountryCode: IsoCountryCode[feCountryCode] },
    // Apollo client loading state gets stuck: https://github.com/apollographql/react-apollo/issues/3425
    // Temporary fix while we wait for a stable 3.0.0 version
    fetchPolicy: 'cache-and-network',
    skip: !user,
  });

  const { cognitoId, details } = user || {};
  const { defaultPaymentAccountId: defaultPrepaidPaymentMethodId } = details || {};

  // note: defaultFdAccountId is deprecated. use defaultAccountIdentifier if present
  const defaultAccountIdentifier = details?.defaultAccountIdentifier || details?.defaultFdAccountId;

  const [checkoutPaymentMethodId, setCheckoutPaymentMethodId] = useState('');
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState('');
  const [defaultReloadPaymentMethodId, setDefaultReloadPaymentMethodId] = useState('');
  const [prepaidReloadPaymentMethodId, setPrepaidReloadPaymentMethodId] = useState('');
  const [paymentMethodHasBeenInit, setPaymentMethodHasBeenInit] = useState(false);
  const [savingReloadPaymentMethod, setSavingReloadPaymentMethod] = useState(false);
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);

  const saveNewPaymentMethodEvent = useCallback(
    (success: boolean, message?: string, context: Record<string, any> = {}) => {
      logEvent(CustomEventNames.SAVE_NEW_PAYMENT_METHOD, EventTypes.Other, {
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message,
        'Payment Processor': context.paymentProcessor,
      });
      if (success) {
        logger.info({
          message: 'Payment method added',
          context,
        });
      } else {
        logger.error({
          message: 'Error adding payment method',
          context,
        });
      }
    },
    []
  );

  const deletePaymentMethodEvent = useCallback((success: boolean, message?: string) => {
    logEvent(CustomEventNames.DELETE_PAYMENT_METHOD, EventTypes.Other, {
      Response: success ? 'Successful' : 'Failure',
      'Response Description': success ? 'Successful' : message,
    });
  }, []);

  const logUserPaymentIdentity = useCallback(
    (accountIdentifier: string) => {
      if (!user || !details) {
        return;
      }
      const selectedMethod = paymentMethods.find(
        method =>
          method.accountIdentifier === accountIdentifier || method.fdAccountId === accountIdentifier
      );
      if (!selectedMethod) {
        return;
      }
      let ccToken;
      if (selectedMethod.credit) {
        ccToken = selectedMethod.credit.panToken;
      } else if (selectedMethod.prepaid) {
        ccToken = selectedMethod.prepaid.cardNumber;
      }

      if (ccToken) {
        updateAmplitudeUserAttributes({
          ccToken,
        });
      }
    },
    [details, paymentMethods, user]
  );

  const setAndLogCheckoutPaymentMethodId = useCallback(
    (fdAccountId: string) => {
      logUserPaymentIdentity(fdAccountId);
      setCheckoutPaymentMethodId(fdAccountId);
    },
    [logUserPaymentIdentity, setCheckoutPaymentMethodId]
  );

  const setAndLogDefaultReloadPaymentMethodId = useCallback(
    async (accountId: string) => {
      setSavingReloadPaymentMethod(true);
      setPrepaidReloadPaymentMethodId(accountId);
      setDefaultReloadPaymentMethodId(accountId);
      try {
        if (user) {
          await updateUserInfo(
            { defaultReloadPaymentMethodId: accountId },
            { skipIdentityUpdates: true }
          );
        }
      } catch (error) {
        logger.error({
          error,
          message: 'Error saving default reload payment method preference',
          context: { accountId },
        });
      } finally {
        setSavingReloadPaymentMethod(false);
      }
    },
    [user, updateUserInfo]
  );

  const updatePaymentMethodsState = useCallback(
    (
      validDefaultPaymentMethodId: string,
      validDefaultReloadPaymentMethodId: string,
      shouldMuteUpdateUserInfoErrors?: boolean
    ) => {
      if (checkoutPaymentMethodId === validDefaultPaymentMethodId || isQuickPayMethodSelected) {
        return;
      }
      /*
      call updateMe query only if user has a invalid defaultAccountIdentifier
      or if it's different than the validDefaultPaymentMethodId returned by the getPaymentMethodsState hook
      */
      const shouldUpdateUser =
        (!user?.details.defaultAccountIdentifier && !!validDefaultPaymentMethodId) ||
        validDefaultPaymentMethodId !== (user?.details.defaultAccountIdentifier ?? '');

      setDefaultPaymentMethodId(validDefaultPaymentMethodId);
      setCheckoutPaymentMethodId(validDefaultPaymentMethodId);
      setPrepaidReloadPaymentMethodId(validDefaultReloadPaymentMethodId);
      setDefaultReloadPaymentMethodId(validDefaultReloadPaymentMethodId);

      if (user && shouldUpdateUser && !isQuickPayMethodSelected) {
        // Update the checkout and reload payment method when adding a new CC
        updateUserInfo(
          {
            defaultCheckoutPaymentMethodId: validDefaultPaymentMethodId,
            defaultReloadPaymentMethodId: validDefaultReloadPaymentMethodId,
          },
          {
            shouldMuteUserInfoErrors: shouldMuteUpdateUserInfoErrors,
            skipIdentityUpdates: true,
          }
        );
      }
    },
    [checkoutPaymentMethodId, updateUserInfo, user, isQuickPayMethodSelected]
  );

  const setAndLogDefaultPaymentMethodId = useCallback(
    async (accountId: string, isAddingNewCc?: boolean) => {
      setSavingPaymentMethod(true);
      logUserPaymentIdentity(accountId);
      setDefaultPaymentMethodId(accountId);
      // Set the checkout payment method when the user change its default one
      setCheckoutPaymentMethodId(accountId);

      try {
        if (user && !isQuickPayMethodSelected) {
          // Update the checkout and reload payment method when adding a new CC
          await updateUserInfo(
            {
              defaultCheckoutPaymentMethodId: accountId,
              ...(isAddingNewCc ? { defaultReloadPaymentMethodId: accountId } : {}),
            },
            { skipIdentityUpdates: true }
          );
        }
      } catch (error) {
        logger.error({
          error,
          message: 'Error saving default checkout payment method preference',
          context: { accountId, isAddingNewCc },
        });
      } finally {
        setSavingPaymentMethod(false);
      }
    },
    [logUserPaymentIdentity, user, updateUserInfo, isQuickPayMethodSelected]
  );

  const getEncryptionDetails = useCallback(async () => {
    const encryptionDetailsResponse = await getEncryptionDetailsMutation();

    if (!encryptionDetailsResponse.data) {
      throw new Error('Missing encryption details');
    }

    return encryptionDetailsResponse.data.encryptionDetails;
  }, [getEncryptionDetailsMutation]);

  const clearPaymentMethods = useCallback(() => {
    setPaymentMethods(old => {
      // if old 0 new 0 no need to cause re-renders
      if (old?.length === 0) {
        return old;
      }
      return [];
    });
    setHasGetPaymentMethodsError(false);
    setLoading(false);
  }, []);

  const getPaymentMethods = useCallback(async () => {
    try {
      if (cognitoId) {
        await refetchPaymentMethods();
      }
    } catch (error) {
      logger.error({
        error,
        message: 'Error getting payment methods',
      });
      clearPaymentMethods();
      setHasGetPaymentMethodsError(true);
      openErrorDialog({
        message: t('paymentLoadingError'),
        modalAppearanceEventMessage: 'Error: Fetching Payment Methods Failure',
        error: toError(error),
      });
    }
  }, [refetchPaymentMethods, clearPaymentMethods, openErrorDialog, cognitoId]);

  const oneTimePaymentMethod: IPaymentMethod | undefined = useMemo(() => {
    return transformPaymentDetailsToPaymentMethod(paymentDetails);
  }, [paymentDetails]);

  /**
   * set up a bunch of state related to a customer's payment accounts and methods
   * @param {IPaymentMethod[]} accounts - list of ways a customer could pay (prepaid, credit card, mobile wallets etc)
   * @param {string} paymentMethodId - attempt to use this as the default payment method
   * @param {string} prepaidPaymentMethodId - attempt to use this as the default prepaid payment method
   */
  const initPaymentMethods = useCallback(
    (
      { accounts, paymentMethodId, prepaidPaymentMethodId }: IInitPaymentMethods,
      shouldMuteUpdateUserInfoErrors?: boolean
    ) => {
      const accountsWithOneTimePaymentMethod = [
        ...(isCheckoutV2Enabled && !!oneTimePaymentMethod ? [oneTimePaymentMethod] : []),
        ...accounts,
      ];
      // Get valid payment methods
      const {
        availablePaymentMethodList,
        validDefaultPaymentMethodId,
        validDefaultReloadPaymentMethodId,
      } = getPaymentMethodsState({
        paymentMethodId,
        prepaidPaymentMethodId,
        accounts: accountsWithOneTimePaymentMethod,
        canUseApplePay,
        enableCashPayment,
        enableCashApp,
        enablePayPal,
        enableVenmo,
        applePayCardDetails,
        canUseGooglePay,
        googlePayCardDetails,
        paymentMethodHasBeenInit,
        braintreeAuthorizationKey,
      });

      // Setup the payment methods into a valid state
      setPaymentMethods(old => {
        // if old 0 new 0 no need to cause re-renders
        if (old?.length === 0 && availablePaymentMethodList?.length === 0) {
          return old;
        }

        return availablePaymentMethodList;
      });

      // skip update payment methods when selected method is one time payment
      if (paymentMethodId === CurrentSelectedMethodType.ADD_NEW_CARD) {
        return;
      }

      updatePaymentMethodsState(
        validDefaultPaymentMethodId,
        validDefaultReloadPaymentMethodId,
        shouldMuteUpdateUserInfoErrors
      );
    },
    [
      applePayCardDetails,
      braintreeAuthorizationKey,
      canUseGooglePay,
      oneTimePaymentMethod,
      canUseApplePay,
      enableCashPayment,
      enableCashApp,
      isCheckoutV2Enabled,
      enablePayPal,
      enableVenmo,
      googlePayCardDetails,
      paymentMethodHasBeenInit,
      updatePaymentMethodsState,
    ]
  );

  useEffect(() => {
    // To avoid a race condition detected with the updateUserInfo while the user is re-authenticating, payment method initialization is skipped.
    // Additionally, we need to prevent the clearing of payment methods since we are not initializing them during user re-authentication.
    if (!user && !isReAuthenticating) {
      clearPaymentMethods();
      return;
    }
    if (userAccountsData) {
      const accounts: IPaymentMethod[] =
        (userAccountsData.userAccounts &&
          userAccountsData.userAccounts.accounts &&
          userAccountsData.userAccounts.accounts.filter<IPaymentMethod>(
            (account): account is IPaymentMethod =>
              !!account && (!!account.fdAccountId || !!account.accountIdentifier)
          )) ||
        [];

      setHasGetPaymentMethodsError(false);

      if (accounts.length) {
        storePrepaidCard({ accounts });
      }
    }
    setLoading(false);
  }, [clearPaymentMethods, user, userAccountsData, isReAuthenticating]);

  const { reloadPrepaidsCardMutation, loading: prepaidLoading } = usePrepaidsReload({
    openErrorDialog,
  });

  const getPrepaidPaymentMethod = useCallback(() => {
    return paymentMethods.find(method => Boolean(method.prepaid)) || null;
  }, [paymentMethods]);

  const getBalanceFromPaymentMethods = useCallback((prepaidPaymentMethod: IPaymentMethod) => {
    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      if (prepaidPaymentMethod.prepaid.feFormattedCurrentBalance) {
        return prepaidPaymentMethod.prepaid.feFormattedCurrentBalance;
      } else if (prepaidPaymentMethod.prepaid.currentBalance) {
        return prepaidPaymentMethod.prepaid.currentBalance;
      }
    }
    return 0;
  }, []);

  const getPrepaidCardNumber = useCallback(() => {
    const prepaidPaymentMethod = getPrepaidPaymentMethod();

    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      // add spaces between every 4 characters in the card number
      return (prepaidPaymentMethod.prepaid.cardNumber.match(/.{1,4}/g) || []).join(' ');
    }
    return null;
  }, [getPrepaidPaymentMethod]);

  const deletePaymentMethod = useCallback(
    async (accountIdentifier: string) => {
      try {
        setIsDeleting(true);
        const { data } = await fireDeleteAccountMutation({
          variables: { input: { storedPaymentMethodId: accountIdentifier } },
        });
        if (data?.deletePaymentMethod.success) {
          // Strip the paymentMethods list from the deleted CC
          const updatedPaymentMethods = paymentMethods.filter(
            method => method.accountIdentifier !== accountIdentifier
          );
          // If the default payment method is the deleted one, reset the payment method using the 1st in the updated array
          const paymentMethodIdAfterDelete =
            defaultAccountIdentifier !== accountIdentifier
              ? defaultAccountIdentifier
              : updatedPaymentMethods[0]?.accountIdentifier;

          // Re-init the payment methods state to make sure we're in a fully working state after deleting a CC
          initPaymentMethods({
            accounts: updatedPaymentMethods,
            paymentMethodId: paymentMethodIdAfterDelete || '',
            prepaidPaymentMethodId: paymentMethodIdAfterDelete || '',
          });

          deletePaymentMethodEvent(true);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error({ error, message: 'Error deleting payment method' });

        deletePaymentMethodEvent(false, errorMessage);
        openErrorDialog({
          message: t('paymentDeletingError'),
          modalAppearanceEventMessage: 'Error: Deleting Payment Method Failure',
          error: toError(error),
        });
      } finally {
        setIsDeleting(false);
      }
    },
    [
      fireDeleteAccountMutation,
      paymentMethods,
      defaultAccountIdentifier,
      initPaymentMethods,
      deletePaymentMethodEvent,
      openErrorDialog,
    ]
  );

  const paymentProcessor = userAccountsData?.userAccounts?.paymentProcessor;
  const isFirstData = paymentProcessor === PaymentProcessor.FIRSTDATA;
  const isOrbital = paymentProcessor === PaymentProcessor.ORBITAL;

  // Expected value that is applied to hidden form fields for payment method
  const hiddenFieldValue = null;

  const transformPaymentValues = useCallback(
    ({
      paymentValues,
      paymentFieldVariations = defaultPaymentFieldVariation,
    }: {
      paymentValues: IPaymentState;
      paymentFieldVariations?: PaymentFieldVariations;
    }): IPaymentPayload => {
      let expiryDate: { month: string; year: string } | null = null;
      const cardNumber = sanitizeNumber(paymentValues.cardNumber);
      const { expiryMonth, expiryYear } = splitExpiry(paymentValues.expiry ?? '');
      expiryDate = paymentValues.expiry
        ? {
            month: expiryMonth,
            year: expiryYear,
          }
        : null;

      const country = paymentValues.billingCountry ? ISOs[paymentValues.billingCountry] : null;
      const postalCode = sanitizeAlphanumeric(paymentValues.billingZip);
      return {
        billingAddress: {
          country: paymentFieldVariations.country ? country : hiddenFieldValue,
          postalCode: paymentFieldVariations.zip ? postalCode : '',
          unitNumber: paymentFieldVariations.addressLine2
            ? paymentValues.billingApt
            : hiddenFieldValue,
          locality: paymentFieldVariations.city ? paymentValues.billingCity : hiddenFieldValue,
          region: paymentFieldVariations.state ? paymentValues.billingState : hiddenFieldValue,
          streetAddress: paymentFieldVariations.addressLine1
            ? paymentValues.billingStreetAddress
            : hiddenFieldValue,
        },
        cardNumber,
        cardType: paymentValues.cardType ?? '',
        expiryDate,
        nameOnCard: paymentValues.nameOnCard,
        securityCode: paymentValues.cvv,
      };
    },
    [hiddenFieldValue]
  );

  const [encryptionResult, setEncryptionResult] = useState<IEncryptionResult | undefined>();

  const addPaymentMethod = useCallback(
    async (
      paymentValues: IPaymentPayload,
      { skipErrorDialogOnError = false, isReloadMethod = false }: IAddPaymentMethodOptions = {},
      encryptionResult?: IEncryptionResult
    ) => {
      const { billingAddress, cardNumber, nameOnCard: fullName } = paymentValues;
      const { accountToDelete, ...cleanedPaymentValues } = paymentValues;

      if (!loading) {
        setLoading(true);
      }

      try {
        // If we're re-vaulting, delete the payment method before adding the new one
        if (accountToDelete) {
          await deletePaymentMethod(accountToDelete);
        }

        let addAccount;
        let accountIdentifier;

        // Handles adding a payment method when the payment processor is Orbital
        if (isOrbital) {
          if (!encryptionResult) {
            throw new Error(
              'Orbital encryption results encryption results not available for add payment method'
            );
          }
          const orbitalEncryptionResult = encryptionResult;

          if (!orbitalEncryptionResult || !('cryptCard' in orbitalEncryptionResult)) {
            throw new Error('Orbital Encryption Failure');
          }

          const orbitalInput: IAddOrbitalAccountInput = {
            encryptedCardNum: orbitalEncryptionResult.cryptCard,
            encryptedCvv: orbitalEncryptionResult.cryptCvv,
            expiryMonth: paymentValues.expiryDate?.month ?? '',
            expiryYear: paymentValues.expiryDate?.year ?? '',
            pieFormat: orbitalEncryptionResult.pieFormat,
            pieMode: orbitalEncryptionResult.mode,
            piePhaseId: String(orbitalEncryptionResult.phase),
            pieIntegrityCheck: orbitalEncryptionResult.integrityCheck,
            pieSubscriberId: orbitalEncryptionResult.subscriberId,
            pieKeyID: orbitalEncryptionResult.keyId,
            cardBrand: paymentValues.cardType.toLowerCase(),
            bin: cardNumber.substring(0, 6),
          };
          const addAccountResponse = await addCreditAccountMutation({
            variables: {
              input: {
                billingAddress,
                orbitalInput,
                fullName: paymentValues.nameOnCard,
              },
            },
          });

          // Assertion of minimum data for add account.
          if (!addAccountResponse.data?.addCreditAccount.accountIdentifier) {
            throw new Error('Add Account Failure');
          }

          addAccount = addAccountResponse.data.addCreditAccount;
          accountIdentifier = addAccount.accountIdentifier ?? '';
        } else {
          // Handles adding a payment method when the payment processor is First Data
          const ccBin = cardNumber.slice(0, 6);

          let firstDataInput: IAddAccountInput = { ccBin: '', fdAccessToken: '', fdNonce: '' };
          let addAccountResponse: any = {};

          // If we're re-vaulting, delete the payment method before adding the new one
          if (accountToDelete) {
            await deletePaymentMethod(accountToDelete);
          }

          const {
            fdPublicKey,
            fdApiKey,
            fdAccessToken,
            fdCustomerId, // Necessary second request
            algorithm,
          } = await getEncryptionDetails();

          if (!fdAccessTokenFirstRequest.current) {
            fdAccessTokenFirstRequest.current = fdAccessToken;
          }

          const nonceResponse = await getNonce(
            cleanedPaymentValues,
            fdPublicKey,
            fdApiKey,
            fdAccessToken,
            fdCustomerId ?? null,
            fdUrl,
            onlySendPostalCode,
            algorithm ?? undefined
          );
          const { token } = await nonceResponse.json();
          const fdNonce = token.tokenId;

          firstDataInput = {
            fdAccessToken,
            fdNonce,
            ccBin,
          };

          try {
            addAccountResponse = await addCreditAccountMutation({
              variables: {
                input: {
                  billingAddress,
                  firstDataInput,
                  fullName,
                },
              },
            });
          } catch (error) {
            throw error;
          }

          // Assertion of minimun data for add account.
          if (
            !addAccountResponse.data?.addCreditAccount.accountIdentifier &&
            !addAccountResponse.data?.addCreditAccount.fdAccountId
          ) {
            throw new Error('Add Account Failure');
          }

          addAccount = addAccountResponse.data.addCreditAccount;
          accountIdentifier = addAccount.accountIdentifier ?? addAccount.fdAccountId ?? '';
        }

        saveNewPaymentMethodEvent(true, undefined, { accountIdentifier });

        logRBIEvent(
          {
            name: CustomEventNames.NEW_PAYMENT_METHOD_SAVED,
            type: EventTypes.Other,
            attributes: {
              paymentMethod: 'CreditCard',
              cardBrand: addAccount?.credit?.cardType,
            },
          },
          { skipLoggingToBraze: true }
        );

        const isReloadPaymentMethod = isReloadMethod && isCheckoutV2Enabled;

        // When a user adds a CC, set the Checkout and Reload Default payment method to the newly added one
        initPaymentMethods({
          accounts: [...paymentMethods, addAccount as IPaymentMethod],
          paymentMethodId: isReloadPaymentMethod ? checkoutPaymentMethodId : accountIdentifier,
          prepaidPaymentMethodId: isReloadPaymentMethod
            ? checkoutPaymentMethodId
            : accountIdentifier,
        });

        if (isReloadPaymentMethod) {
          await setAndLogDefaultReloadPaymentMethodId(accountIdentifier);
        } else {
          await setAndLogDefaultPaymentMethodId(accountIdentifier, true);
        }

        return accountIdentifier;
      } catch (error) {
        logger.error({
          error,
          message: 'Error adding payment method',
        });
        const err = toError(error);
        saveNewPaymentMethodEvent(false, err.message, {
          accountIdentifier: undefined,
          message: err.message,
        });
        if (!skipErrorDialogOnError) {
          const rawStatusCode = (error as {
            graphQLErrors?: GraphQLError[];
          }).graphQLErrors?.find((graphQLError: GraphQLError) =>
            Number.isInteger(graphQLError?.extensions?.statusCode)
          )?.extensions?.statusCode;
          const statusCode: number | undefined =
            typeof rawStatusCode === 'number' ? rawStatusCode : undefined;
          openErrorDialog({
            message:
              statusCode === HttpErrorCodes.TooManyRequests
                ? t('tooManyAddAccountAttempts')
                : t('paymentAddingError'),
            modalAppearanceEventMessage: 'Error: Adding Payment Method Failure',
            error: err,
          });
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      addCreditAccountMutation,
      checkoutPaymentMethodId,
      deletePaymentMethod,
      fdUrl,
      getEncryptionDetails,
      initPaymentMethods,
      isCheckoutV2Enabled,
      isOrbital,
      loading,
      onlySendPostalCode,
      openErrorDialog,
      paymentMethods,
      saveNewPaymentMethodEvent,
      setAndLogDefaultPaymentMethodId,
      setAndLogDefaultReloadPaymentMethodId,
    ]
  );

  const reloadPrepaidCard = useCallback(
    async (reloadInfo: IReloadPrepaidCard) => {
      try {
        const { currencyCode, cardType, fundValue } = reloadInfo;

        const { data } = await reloadPrepaidsCardMutation(reloadInfo);

        logEvent(CustomEventNames.BUTTON_CLICK_TIMS_CARD_RELOAD, EventTypes.Transaction, {
          Currency: currencyCode,
          'Card Type': cardType,
          'Reload Amount': fundValue / 100,
        });

        return data?.prepaidsReload?.currentBalance;
      } catch (error) {
        logger.error({
          error,
          message: 'Error reloading prepaid card',
        });
        return undefined;
      }
    },
    [logEvent, reloadPrepaidsCardMutation]
  );

  const mergePrepaidCardBalances = useCallback(
    async ({ sourceCardNumber, sourcePin }: IMergePrepaidInput): Promise<IMergePrepaidPayload> => {
      if (!loading) {
        setLoading(true);
      }

      try {
        const { data: wrapper } = await fireMergePrepaidMutation({
          variables: {
            input: {
              sourceCardNumber: sourceCardNumber.replace(/\s/g, ''),
              sourcePin,
            },
          },
        });

        const data = wrapper!.mergePrepaid;

        await getPaymentMethods();

        // Set the newly added GC, or the desitination GC as the checkout payment method.
        setCheckoutPaymentMethodId(data.storedPaymentMethodId || '');

        return data;
      } finally {
        setLoading(false);
      }
    },
    [fireMergePrepaidMutation, getPaymentMethods, loading]
  );

  const checkoutPaymentMethod = paymentMethods.find(
    ({ accountIdentifier, fdAccountId }) =>
      accountIdentifier === checkoutPaymentMethodId || fdAccountId === checkoutPaymentMethodId
  );

  // The initialization of payment methods for checkout redesign requires different dependencies
  // to function correctly.
  const initializePaymentMethodsDependenciesArray = isCheckoutV2Enabled
    ? [
        oneTimePaymentMethod,
        isCheckoutV2Enabled,
        cognitoId,
        enableCashPayment,
        defaultAccountIdentifier,
        enablePayPal,
        enableVenmo,
        userAccountsData?.userAccounts?.accounts,
        isReAuthenticating,
        enableCashApp,
        defaultPrepaidPaymentMethodId,
        canUseApplePay,
        canUseGooglePay,
      ]
    : [
        cognitoId,
        enableCashPayment,
        defaultAccountIdentifier,
        userAccountsData?.userAccounts?.accounts,
        enablePayPal,
        enableVenmo,
        enableCashApp,
        canUseApplePay,
        canUseGooglePay,
      ];

  useEffect(
    () => {
      // Initialize the payment method when the app loads
      if (userAccountsData?.userAccounts?.accounts && !isReAuthenticating) {
        const { accounts } = userAccountsData.userAccounts;

        /**
         * Unlike Apple Pay and Google Pay, the cash method can be a default payment method
         * so add it before `initPaymentMethods` runs so it can be sorted & stored as the default method in FE state
         */
        const accountsMaybeWithCash = enableCashPayment ? [...accounts, cashAccount] : accounts;

        setPaymentMethodHasBeenInit(true);
        // When we have the user's payment method list, initialize the payment methods state

        const paymentMethodId =
          isCheckoutV2Enabled && checkoutPaymentMethodId === CurrentSelectedMethodType.ADD_NEW_CARD
            ? checkoutPaymentMethodId
            : defaultAccountIdentifier || '';

        initPaymentMethods(
          {
            accounts: accountsMaybeWithCash as IPaymentMethod[],
            paymentMethodId,
            prepaidPaymentMethodId: defaultPrepaidPaymentMethodId || '',
          },
          true
        );
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    initializePaymentMethodsDependenciesArray
    /* eslint-enable react-hooks/exhaustive-deps */
  );

  // This state ensures that no payment methods are available after loading the user's account information.
  // Native wallets (Apple Pay / Google Pay) count as available methods even when the user has no saved cards.
  const noPaymentMethodsAvailable = useMemo(
    () =>
      !!userAccountsData?.userAccounts &&
      !filterExpiredCreditCards(paymentMethods).length &&
      !canUseApplePay &&
      !canUseGooglePay,
    [paymentMethods, userAccountsData?.userAccounts, canUseApplePay, canUseGooglePay]
  );

  const savingPaymentMethods = useMemo(() => {
    return (savingPaymentMethod || savingReloadPaymentMethod) && isCheckoutV2Enabled;
  }, [isCheckoutV2Enabled, savingPaymentMethod, savingReloadPaymentMethod]);

  return {
    loading: loading || accountsLoading || prepaidLoading || isDeleting,
    // savingPaymentMethods state is not included in the 'loading' state to prevent the
    // payment methods page from reloading every time a default payment method is selected.
    savingPaymentMethods,
    noPaymentMethodsAvailable,
    paymentMethods,
    getPaymentMethods,
    getBalanceFromPaymentMethods,
    getPrepaidPaymentMethod,
    getPrepaidCardNumber,
    getEncryptionDetails,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    setCheckoutPaymentMethodId: setAndLogCheckoutPaymentMethodId,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    encryptionResult,
    setEncryptionResult,
    setDefaultPaymentMethodId: setAndLogDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId: setAndLogDefaultReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    prepaidReloadPaymentMethodId,
    addPaymentMethod,
    deletePaymentMethod,
    reloadPrepaidCard,
    mergePrepaidCardBalances,
    hasGetPaymentMethodsError,
    canUseApplePay,
    canUseGooglePay,
    paymentProcessor,
    isFirstData,
    isOrbital,
    transformPaymentValues,
    setLoading,
  };
};
